import React from 'react';
import { ContentGrid } from '../ContentGrid/ContentGrid';
import type { ContentGridLayout } from '../ContentGrid/ContentGrid';
import type { CustomIconName } from '../CustomIcon/CustomIcon';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import type { CtaAction } from '../../utils/ctaAction';

/**
 * StepsSection — numbered step cards in a responsive grid.
 *
 * Thin wrapper over ContentGrid with `iconStyle="step"` hardcoded. Exists as
 * its own block in the Poly CMS editor so authors can pick "Steps Section"
 * from the block picker and get step-specific field labels.
 *
 * Each step renders a 48px numbered circle badge above the heading and
 * description. With `stepNumbers` enabled the badge is an auto-generated
 * numeral (1, 2, 3, …) — no per-step artwork required; otherwise a pre-made
 * SVG supplied via item{N}IconUrl is used. Supports up to 6 steps, and a step
 * may carry description text with no heading of its own.
 *
 * `layout="sticky-rail"` moves the section header into a pinned left column
 * alongside an optional portrait image.
 *
 * @polyType Block
 * @polyLabel Steps Section
 */

/** Flat, per-step props for slots 1-6. */
export interface StepsSectionSlotProps {
  /** @polyLabel Step 1 Heading */
  item1Heading?: string;
  /**
   * Step 1 body copy. Markdown: inline links and blank-line paragraph breaks are kept.
   * @polyType RichText
   * @polyLabel Step 1 Description
   */
  item1Text?: string;
  /**
   * Step 1 numbered badge SVG URL (48×48).
   * @polyType Url
   * @polyLabel Step 1 Icon
   */
  item1IconUrl?: string;
  /** @polyLabel Step 1 CTA Text */
  item1CtaText?: string;
  /**
   * @polyType Url
   * @polyLabel Step 1 CTA Link
   */
  item1CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Step 1 CTA Behavior
   * @polyTranslatable false
   */
  item1CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  item1CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item1CtaTargetPopupId?: string;

  /** @polyLabel Step 2 Heading */
  item2Heading?: string;
  /**
   * Step 2 body copy. Markdown: inline links and blank-line paragraph breaks are kept.
   * @polyType RichText
   * @polyLabel Step 2 Description
   */
  item2Text?: string;
  /**
   * @polyType Url
   * @polyLabel Step 2 Icon
   */
  item2IconUrl?: string;
  /** @polyLabel Step 2 CTA Text */
  item2CtaText?: string;
  /**
   * @polyType Url
   * @polyLabel Step 2 CTA Link
   */
  item2CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Step 2 CTA Behavior
   * @polyTranslatable false
   */
  item2CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  item2CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item2CtaTargetPopupId?: string;

  /** @polyLabel Step 3 Heading */
  item3Heading?: string;
  /**
   * Step 3 body copy. Markdown: inline links and blank-line paragraph breaks are kept.
   * @polyType RichText
   * @polyLabel Step 3 Description
   */
  item3Text?: string;
  /**
   * @polyType Url
   * @polyLabel Step 3 Icon
   */
  item3IconUrl?: string;
  /** @polyLabel Step 3 CTA Text */
  item3CtaText?: string;
  /**
   * @polyType Url
   * @polyLabel Step 3 CTA Link
   */
  item3CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Step 3 CTA Behavior
   * @polyTranslatable false
   */
  item3CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  item3CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item3CtaTargetPopupId?: string;

  /** @polyLabel Step 4 Heading */
  item4Heading?: string;
  /**
   * Step 4 body copy. Markdown: inline links and blank-line paragraph breaks are kept.
   * @polyType RichText
   * @polyLabel Step 4 Description
   */
  item4Text?: string;
  /**
   * @polyType Url
   * @polyLabel Step 4 Icon
   */
  item4IconUrl?: string;
  /** @polyLabel Step 4 CTA Text */
  item4CtaText?: string;
  /**
   * @polyType Url
   * @polyLabel Step 4 CTA Link
   */
  item4CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Step 4 CTA Behavior
   * @polyTranslatable false
   */
  item4CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  item4CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item4CtaTargetPopupId?: string;

  /** @polyLabel Step 5 Heading */
  item5Heading?: string;
  /**
   * Step 5 body copy. Markdown: inline links and blank-line paragraph breaks are kept.
   * @polyType RichText
   * @polyLabel Step 5 Description
   */
  item5Text?: string;
  /**
   * @polyType Url
   * @polyLabel Step 5 Icon
   */
  item5IconUrl?: string;
  /** @polyLabel Step 5 CTA Text */
  item5CtaText?: string;
  /**
   * @polyType Url
   * @polyLabel Step 5 CTA Link
   */
  item5CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Step 5 CTA Behavior
   * @polyTranslatable false
   */
  item5CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  item5CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item5CtaTargetPopupId?: string;

  /** @polyLabel Step 6 Heading */
  item6Heading?: string;
  /**
   * Step 6 body copy. Markdown: inline links and blank-line paragraph breaks are kept.
   * @polyType RichText
   * @polyLabel Step 6 Description
   */
  item6Text?: string;
  /**
   * @polyType Url
   * @polyLabel Step 6 Icon
   */
  item6IconUrl?: string;
  /** @polyLabel Step 6 CTA Text */
  item6CtaText?: string;
  /**
   * @polyType Url
   * @polyLabel Step 6 CTA Link
   */
  item6CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Step 6 CTA Behavior
   * @polyTranslatable false
   */
  item6CtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  item6CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  item6CtaTargetPopupId?: string;
}

/**
 * @polySemanticUsage About this block: an ordered, numbered sequence of steps ("how it works",
 * "get started in 3 steps"). Use it whenever the source presents 2-6 sequential steps; use
 * ContentGrid for equal-weight items with no order and CardGrid for self-contained cards.
 * Step text (item{N}Text) is markdown: keep the source's inline links as [label](url) and keep
 * its paragraph breaks as blank lines — each paragraph renders as its own paragraph inside the
 * step. Never flatten multi-paragraph step copy into one run of text or strip its links. A step
 * may have body text with no heading.
 * Layout: if the source section shows an image or illustration beside the steps, set
 * layout to sticky-rail and put that image in railImageSrc (with railImageAlt) — the header and
 * image pin in a left rail while the steps stack on the right. Use the default grid layout only
 * when the source has no accompanying image. Never drop the image.
 * Numbering: set stepNumbers when the source shows plain numerals (1, 2, 3) or no per-step
 * artwork; use item{N}IconUrl only when each step has its own distinct icon.
 * Section title and subtitle: any heading/intro text that introduces the steps in the source
 * MUST map to this block's heading and subheading props — never a separate RichText block.
 * Link behavior: always leave item{N}CtaBehavior / sectionCtaBehavior unset (same tab), even when
 * that source link opens in a new tab or is an External Link type. Migration never sets newTab;
 * editors add it by hand for the few exceptions. Never infer it from the destination host.
 */
export interface StepsSectionProps extends StepsSectionSlotProps {
  /**
   * Badge pill text shown above the heading.
   * @polyLabel Section Badge
   */
  sectionBadge?: string;
  /**
   * CustomIcon name rendered inside the badge pill.
   * @polyType SelectList
   * @polyOptions Star=star,Bolt=bolt,Cart=cart,Check=check,Package=package,Analytics=analytics
   * @polyDefault star
   * @polyTranslatable false
   * @polyLabel Badge Icon
   */
  sectionBadgeIcon?: CustomIconName;
  /**
   * Section heading (renders as h2).
   * @polyLabel Heading
   */
  heading?: string;
  /**
   * Section subheading.
   * @polyLabel Subheading
   */
  subheading?: string;
  /**
   * CTA button label rendered next to the heading.
   * @polyLabel Section CTA Text
   */
  sectionCtaText?: string;
  /**
   * CTA button href.
   * @polyType Url
   * @polyLabel Section CTA Link
   */
  sectionCtaHref?: string;
  /**
   * Whether the section CTA opens in the current tab or a new one.
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Section CTA Behavior
   * @polyTranslatable false
   */
  sectionCtaBehavior?: CtaBehavior;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  sectionCtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  sectionCtaTargetPopupId?: string;
  /**
   * Background color.
   * @polyOptions White=white,Off-White=off-white
   * @polyDefault white
   * @polyLabel Background
   */
  background?: 'white' | 'off-white';
  /**
   * Render auto-generated numbered circle badges (1, 2, 3, …) for each step
   * instead of requiring a per-step SVG via item{N}IconUrl. On the off-white
   * background the numbered circle is painted white for contrast.
   * @polyType Boolean
   * @polyLabel Step Numbers
   */
  stepNumbers?: boolean;
  /**
   * Placement of the section header relative to the steps.
   * 'grid' (default) puts the header above the steps at full width.
   * 'sticky-rail' moves the header into a 448px left column that stays pinned
   * while the steps scroll in a single stack on the right, collapsing to one
   * column at the tablet breakpoint and below.
   * @polyType SelectList
   * @polyOptions Grid=grid,Sticky left rail=sticky-rail
   * @polyDefault grid
   * @polyLabel Layout
   */
  layout?: ContentGridLayout;
  /**
   * Portrait image pinned beneath the header inside the sticky rail.
   * Ignored unless Layout is 'sticky-rail'.
   * @polyType Url
   * @polyLabel Rail Image
   */
  railImageSrc?: string;
  /**
   * Alt text for the rail image. Leave empty when the image is decorative.
   * @polyLabel Rail Image Alt Text
   */
  railImageAlt?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

export const StepsSection: React.FC<StepsSectionProps> = (props) => {
  return <ContentGrid {...props} iconStyle="step" />;
};
